import { useScrollDepth } from "@/hooks";
import { trackEvent } from "@/lib/dataLayer";
import {
  CookieConsent,
  DemoFormModal,
  FAQ,
  Footer,
  Header,
  WhatsAppFloatButton,
} from "@/pages/landingPage/components";
import { MotionConfig } from "framer-motion";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { BenefitsSection } from "./components/BenefitsSection";
import { CobrancaHero } from "./components/CobrancaHero";
import { CtaBand } from "./components/CtaBand";
import { NetworkSection } from "./components/NetworkSection";
import { PageSection } from "@/pages/landingPage/components/pageSection";
import { ProofSection } from "./components/ProofSection";
import { StepsSection } from "./components/StepsSection";
import {
  BREADCRUMB_SCHEMA,
  DESCRIPTION,
  FAQ_ITEMS,
  OG_TITLE,
  PATH,
  SERVICE_SCHEMA,
  SITE_URL,
  TITLE,
} from "./constants";

type DemoCtaLocation = "header" | "hero" | "cta_band";

export const CobrancaWhatsapp = () => {
  useScrollDepth();
  const [isDemoOpen, setIsDemoOpen] = useState(false);

  const openDemo = (location: DemoCtaLocation) => {
    trackEvent({ event: "cta_click_specialist", location });
    setIsDemoOpen(true);
  };

  return (
    <>
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href={`${SITE_URL}${PATH}`} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Fidexa" />
        <meta property="og:title" content={OG_TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:url" content={`${SITE_URL}${PATH}`} />
        <meta property="og:image" content={`${SITE_URL}/og-image.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="pt_BR" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={OG_TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content={`${SITE_URL}/og-image.png`} />

        <script type="application/ld+json">
          {JSON.stringify(BREADCRUMB_SCHEMA)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(SERVICE_SCHEMA)}
        </script>
      </Helmet>

      {/* reducedMotion="user": toda animação do framer-motion na página
          (inclusive Header e FAQ compartilhados) some para quem pediu menos
          movimento no sistema. */}
      <MotionConfig reducedMotion="user">
        <div className="bg-ds-paper min-h-screen overflow-x-clip">
          <Header onSpecialistClick={() => openDemo("header")} />

          <main className="flex flex-col gap-20 lg:gap-28">
            <CobrancaHero onDemoClick={() => openDemo("hero")} />
            <BenefitsSection />
            <StepsSection />
            <NetworkSection />
            <ProofSection />
            <CtaBand onDemoClick={() => openDemo("cta_band")} />

            {/* FAQ específica de cobrança via WhatsApp — o componente gera o
                FAQPage JSON-LD a partir do mesmo array, sem sobreposição com a
                home ou com /crediario. */}
            <PageSection>
              <FAQ
                items={FAQ_ITEMS}
                headingClassName="text-ds-h2 sm:text-ds-h2 lg:text-ds-h2 text-ds-ink-900 mb-8 text-left lg:mb-10"
              />
            </PageSection>

            <Footer />
          </main>

          <WhatsAppFloatButton />
          <CookieConsent />
          <DemoFormModal
            isOpen={isDemoOpen}
            onClose={() => setIsDemoOpen(false)}
          />
        </div>
      </MotionConfig>
    </>
  );
};
